import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { Card } from '../../design-system/Card';
import { Button } from '../../design-system/Button';
import { Badge } from '../../design-system/Badge';
import { Modal } from '../../design-system/Modal';
import { Input, Textarea } from '../../design-system/Input';
import {
  Globe,
  Sun,
  Moon,
  Share2,
  ShieldCheck,
  CheckCircle2,
  ThumbsUp,
  MessageSquarePlus,
  Search,
  Sparkles,
  Check
} from 'lucide-react';

export const PublicProjectPage = ({ onBackToApp }) => {
  const { theme, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('roadmap'); // 'roadmap', 'feedback', 'changelog', 'milestones'
  const [isCopied, setIsCopied] = useState(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [feedbackSearch, setFeedbackSearch] = useState('');
  const [feedbackCategory, setFeedbackCategory] = useState('all');

  // Submit Feedback Form State
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newCat, setNewCat] = useState('UI/UX');
  const [newEmail, setNewEmail] = useState('');

  const publicProject = {
    key: 'FOR',
    name: 'Forma Platform V1',
    category: 'Software Engineering',
    lead: 'Rohith Prince',
    description:
      'Forma is an open, modular work-management platform built on a dual-shadow neomorphic design system and Node.js AppSail backend. It adapts dynamically to software, game development, hardware, and scientific disciplines.',
    progress: 74,
    updatedAt: '2026-08-22'
  };

  const [feedbackList, setFeedbackList] = useState([
    {
      id: 'fb_1',
      title: 'Dark mode AMOLED true-black background option',
      description: 'Add an optional pure pitch black background for OLED screens and low-light environments.',
      category: 'UI/UX',
      status: 'Under Review',
      votes: 42,
      hasVoted: false,
      convertedTaskId: null
    },
    {
      id: 'fb_2',
      title: 'GitHub Pull Request & CI check status badge inside Task Drawer',
      description: 'Show open PR status, branch reviews, and CI build checks directly in the development section.',
      category: 'Integrations',
      status: 'Planned',
      votes: 89,
      hasVoted: true,
      convertedTaskId: 'FOR-105'
    },
    {
      id: 'fb_3',
      title: 'Hardware Bill of Materials (BOM) Excel / CSV 1-Click Export',
      description: 'Export structured component pricing, supplier links, and package footprints to CSV spreadsheet.',
      category: 'Specialized',
      status: 'In Progress',
      votes: 31,
      hasVoted: false,
      convertedTaskId: 'FOR-106'
    }
  ]);

  const publicRoadmap = {
    planned: [
      { id: 'FOR-105', title: 'GitHub PR Status & CI Check Badges', priority: 'medium', labels: ['Integrations'] },
      { id: 'FOR-107', title: 'External Public Feedback & Voting Portal', priority: 'high', labels: ['Community'] }
    ],
    inProgress: [
      { id: 'FOR-102', title: 'Dynamic Module Routing & Navigation Engine', priority: 'urgent', labels: ['Architecture'] },
      { id: 'FOR-106', title: 'Hardware Bill of Materials CSV Exporter', priority: 'high', labels: ['Specialized'] }
    ],
    completed: [
      { id: 'FOR-101', title: 'Implement Neomorphic Dual-Shadow Theme Engine', priority: 'high', labels: ['Design System'] },
      { id: 'FOR-100', title: 'Workspace Membership & Role-Based Access Control', priority: 'urgent', labels: ['Core'] }
    ]
  };

  const publicChangelog = [
    {
      version: 'v0.9.0-alpha',
      date: 'Aug 22, 2026',
      title: 'Forma Core Modular Alpha Release',
      highlights: [
        'Dual-shadow neomorphic design system for light (#f6fafd) and dark (#111417) themes',
        'Universal task drawer with checklists, subtasks, attachments, and dependencies',
        'Sprint planning, burndown trajectory, and product backlog grooming',
        'Visual no-code automation rules and event triggers',
        'Public project roadmap and changelog portal'
      ]
    },
    {
      version: 'v0.8.0-alpha',
      date: 'Aug 15, 2026',
      title: 'Design System & Navigation Baseline',
      highlights: [
        '16 foundational neomorphic components',
        'Collapsible workspace sidebar and breadcrumb navigation',
        'Workspace and project creation wizards'
      ]
    }
  ];

  const publicMilestones = [
    { id: 'm1', title: 'Design System & Architecture Baseline', targetDate: 'Aug 22, 2026', status: 'done', progress: 100 },
    { id: 'm2', title: 'Sprint 1 Acceptance Review & Community Demo', targetDate: 'Aug 28, 2026', status: 'pending', progress: 70 },
    { id: 'm3', title: 'Public Beta Launch & Ecosystem', targetDate: 'Sep 15, 2026', status: 'pending', progress: 25 }
  ];

  const handleCopyShareLink = () => {
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleVote = (fbId) => {
    setFeedbackList((prev) =>
      prev.map((item) => {
        if (item.id === fbId) {
          const increment = item.hasVoted ? -1 : 1;
          return { ...item, votes: item.votes + increment, hasVoted: !item.hasVoted };
        }
        return item;
      })
    );
  };

  const handleSubmitFeedback = (e) => {
    e?.preventDefault();
    if (!newTitle.trim() || !newDesc.trim()) return;

    const newFb = {
      id: 'fb_' + Date.now(),
      title: newTitle.trim(),
      description: newDesc.trim(),
      category: newCat,
      status: 'Under Review',
      votes: 1,
      hasVoted: true,
      convertedTaskId: null
    };

    setFeedbackList([newFb, ...feedbackList]);
    setNewTitle('');
    setNewDesc('');
    setNewEmail('');
    setIsSubmitModalOpen(false);
  };

  const filteredFeedback = feedbackList.filter((f) => {
    const matchesSearch = f.title.toLowerCase().includes(feedbackSearch.toLowerCase()) || f.description.toLowerCase().includes(feedbackSearch.toLowerCase());
    const matchesCategory = feedbackCategory === 'all' || f.category.toLowerCase() === feedbackCategory.toLowerCase();
    return matchesSearch && matchesCategory;
  });

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--surface-bg)',
        color: 'var(--text-primary)',
        fontFamily: 'var(--font-body)',
        padding: '24px 20px 60px'
      }}
      className="animate-fade-in"
    >
      <div style={{ maxWidth: '1040px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Top Navbar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '34px',
                height: '34px',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: 'var(--accent-teal)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 'var(--shadow-raised-sm)',
                color: 'var(--text-on-accent)',
                fontWeight: '900',
                fontSize: '18px'
              }}
            >
              F
            </div>
            <div>
              <span style={{ fontWeight: '900', fontSize: '16px', letterSpacing: '-0.02em' }}>FORMA</span>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: '6px', fontWeight: '700' }}>
                PUBLIC PORTAL
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Button
              variant="secondary"
              size="sm"
              icon={<Share2 size={13} />}
              onClick={handleCopyShareLink}
            >
              {isCopied ? 'Link Copied!' : 'Share'}
            </Button>

            <button
              onClick={toggleTheme}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: 'var(--surface-recessed)',
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Toggle Theme"
            >
              {theme === 'dark' ? <Sun size={15} color="var(--color-warning)" /> : <Moon size={15} color="var(--color-purple)" />}
            </button>

            {onBackToApp && (
              <Button variant="primary" size="sm" onClick={onBackToApp}>
                Open in Forma App
              </Button>
            )}
          </div>
        </div>

        {/* Project Hero Banner */}
        <Card padding="28px">
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '14px', marginBottom: '16px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <Badge variant="teal" size="sm">{publicProject.key}</Badge>
                <h1 style={{ fontSize: '24px', fontWeight: '800' }}>{publicProject.name}</h1>
                <Badge variant="recessed" size="xs">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <Globe size={11} color="var(--accent-teal)" /> Public Project
                  </span>
                </Badge>
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.6', maxWidth: '780px' }}>
                {publicProject.description}
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Project Lead: {publicProject.lead}</span>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Updated {publicProject.updatedAt}</span>
            </div>
          </div>

          {/* Progress bar */}
          <div style={{ backgroundColor: 'var(--surface-recessed)', padding: '14px 18px', borderRadius: 'var(--radius-md)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
              <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>Overall Roadmap Completion</span>
              <span style={{ fontWeight: '800', color: 'var(--accent-teal)' }}>{publicProject.progress}%</span>
            </div>
            <div style={{ height: '6px', backgroundColor: 'var(--surface-card)', borderRadius: '999px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${publicProject.progress}%`, backgroundColor: 'var(--accent-teal)', borderRadius: '999px' }} />
            </div>
          </div>
        </Card>

        {/* Security Scope Guarantee */}
        <div
          style={{
            padding: '12px 18px',
            backgroundColor: 'rgba(35, 190, 179, 0.08)',
            border: '1px solid rgba(35, 190, 179, 0.3)',
            borderRadius: 'var(--radius-md)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '12px',
            color: 'var(--text-secondary)'
          }}
        >
          <ShieldCheck size={18} color="var(--accent-teal)" />
          <span>
            <strong>Verified Public View:</strong> Internal developer discussions, private security tasks, and member emails are securely masked.
          </span>
        </div>

        {/* Public View Navigation Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border-divider)', gap: '8px' }}>
          {[
            { id: 'roadmap', label: 'Public Roadmap' },
            { id: 'feedback', label: `Feature Requests & Feedback (${feedbackList.length})` },
            { id: 'changelog', label: `Changelog (${publicChangelog.length})` },
            { id: 'milestones', label: `Milestones (${publicMilestones.length})` }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '10px 16px',
                border: 'none',
                background: 'none',
                borderBottom: activeTab === tab.id ? '2px solid var(--accent-teal)' : '2px solid transparent',
                color: activeTab === tab.id ? 'var(--accent-teal)' : 'var(--text-secondary)',
                fontSize: '14px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* TAB 1: ROADMAP */}
        {activeTab === 'roadmap' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
            {/* Planned */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '6px' }}>
                <span style={{ fontSize: '14px', fontWeight: '800', color: 'var(--text-primary)' }}>Planned</span>
                <Badge size="xs" variant="neutral">{publicRoadmap.planned.length}</Badge>
              </div>
              {publicRoadmap.planned.map((t) => (
                <div key={t.id} style={{ padding: '14px 16px', backgroundColor: 'var(--surface-card)', boxShadow: 'var(--shadow-raised-sm)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)' }}>{t.id}</span>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>{t.title}</div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {t.labels.map((l) => (
                      <Badge key={l} size="xs" variant="recessed">{l}</Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* In Progress */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '6px' }}>
                <span style={{ fontSize: '14px', fontWeight: '800', color: 'var(--accent-teal)' }}>In Progress</span>
                <Badge size="xs" variant="teal">{publicRoadmap.inProgress.length}</Badge>
              </div>
              {publicRoadmap.inProgress.map((t) => (
                <div key={t.id} style={{ padding: '14px 16px', backgroundColor: 'var(--surface-card)', boxShadow: 'var(--shadow-raised-sm)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--accent-teal)' }}>{t.id}</span>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>{t.title}</div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {t.labels.map((l) => (
                      <Badge key={l} size="xs" variant="teal">{l}</Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Completed */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '6px' }}>
                <span style={{ fontSize: '14px', fontWeight: '800', color: 'var(--color-success)' }}>Delivered</span>
                <Badge size="xs" variant="success">{publicRoadmap.completed.length}</Badge>
              </div>
              {publicRoadmap.completed.map((t) => (
                <div key={t.id} style={{ padding: '14px 16px', backgroundColor: 'var(--surface-card)', boxShadow: 'var(--shadow-raised-sm)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)' }}>{t.id}</span>
                    <CheckCircle2 size={14} color="var(--color-success)" />
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>{t.title}</div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {t.labels.map((l) => (
                      <Badge key={l} size="xs" variant="recessed">{l}</Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB 2: FEEDBACK & FEATURE REQUESTS */}
        {activeTab === 'feedback' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
              {/* Search & Category filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, maxWidth: '480px' }}>
                <Input
                  placeholder="Search requests..."
                  value={feedbackSearch}
                  onChange={(e) => setFeedbackSearch(e.target.value)}
                  icon={<Search size={15} />}
                />
                <div style={{ display: 'flex', gap: '4px' }}>
                  {['all', 'UI/UX', 'Integrations', 'Specialized'].map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setFeedbackCategory(cat)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: 'var(--radius-pill)',
                        backgroundColor: feedbackCategory === cat ? 'var(--accent-teal)' : 'var(--surface-card)',
                        color: feedbackCategory === cat ? 'var(--text-on-accent)' : 'var(--text-muted)',
                        border: '1px solid var(--border-subtle)',
                        fontSize: '11px',
                        fontWeight: '700',
                        cursor: 'pointer'
                      }}
                    >
                      {cat === 'all' ? 'All' : cat}
                    </button>
                  ))}
                </div>
              </div>

              <Button
                variant="primary"
                size="sm"
                icon={<MessageSquarePlus size={14} />}
                onClick={() => setIsSubmitModalOpen(true)}
              >
                Submit Feature Request
              </Button>
            </div>

            {/* Feedback items list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {filteredFeedback.map((fb) => (
                <Card key={fb.id} padding="20px">
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                    {/* Upvote Button */}
                    <button
                      onClick={() => handleVote(fb.id)}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '10px 14px',
                        borderRadius: 'var(--radius-md)',
                        backgroundColor: fb.hasVoted ? 'rgba(35, 190, 179, 0.15)' : 'var(--surface-recessed)',
                        border: fb.hasVoted ? '1px solid var(--accent-teal)' : '1px solid var(--border-subtle)',
                        color: fb.hasVoted ? 'var(--accent-teal)' : 'var(--text-primary)',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        minWidth: '54px'
                      }}
                    >
                      <ThumbsUp size={16} />
                      <span style={{ fontSize: '13px', fontWeight: '800', marginTop: '2px' }}>
                        {fb.votes}
                      </span>
                    </button>

                    {/* Content */}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <Badge variant="recessed" size="xs">{fb.category}</Badge>
                        <Badge
                          variant={
                            fb.status === 'Completed'
                              ? 'success'
                              : fb.status === 'Planned' || fb.status === 'In Progress'
                              ? 'teal'
                              : 'neutral'
                          }
                          size="xs"
                        >
                          {fb.status}
                        </Badge>
                        {fb.convertedTaskId && (
                          <span style={{ fontSize: '11px', color: 'var(--accent-teal)', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '3px' }}>
                            <Check size={12} /> Roadmap Task: {fb.convertedTaskId}
                          </span>
                        )}
                      </div>

                      <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '6px' }}>
                        {fb.title}
                      </h3>
                      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                        {fb.description}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* TAB 3: CHANGELOG */}
        {activeTab === 'changelog' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {publicChangelog.map((entry) => (
              <Card key={entry.version} padding="24px">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Badge variant="teal" size="sm">{entry.version}</Badge>
                    <h3 style={{ fontSize: '17px', fontWeight: '800' }}>{entry.title}</h3>
                  </div>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{entry.date}</span>
                </div>

                <ul style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                  {entry.highlights.map((h, i) => (
                    <li key={i}>{h}</li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        )}

        {/* TAB 4: MILESTONES */}
        {activeTab === 'milestones' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
            {publicMilestones.map((m) => (
              <Card key={m.id} padding="20px">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: '800' }}>{m.title}</h3>
                  <Badge variant={m.status === 'done' ? 'success' : 'purple'} size="xs">
                    {m.status === 'done' ? 'Delivered' : m.targetDate}
                  </Badge>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-muted)', marginBottom: '6px' }}>
                  <span>Completion</span>
                  <span style={{ fontWeight: '700', color: 'var(--accent-teal)' }}>{m.progress}%</span>
                </div>
                <div style={{ height: '5px', backgroundColor: 'var(--surface-recessed)', borderRadius: '999px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${m.progress}%`, backgroundColor: m.status === 'done' ? 'var(--color-success)' : 'var(--color-purple)', borderRadius: '999px' }} />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* SUBMIT FEEDBACK MODAL */}
      <Modal
        isOpen={isSubmitModalOpen}
        onClose={() => setIsSubmitModalOpen(false)}
        title="Submit Feature Request / Feedback"
        subtitle="Share ideas and improvements with the project maintainers"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsSubmitModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmitFeedback} icon={<Sparkles size={14} />}>
              Submit Request
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmitFeedback} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <Input
            label="Feature Title *"
            placeholder="e.g., Export Task Gantt chart to high-res PNG"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            required
            autoFocus
          />

          <div>
            <label style={{ fontSize: '13px', fontWeight: '700', marginBottom: '6px', display: 'block' }}>
              Category
            </label>
            <div style={{ display: 'flex', gap: '6px' }}>
              {['UI/UX', 'Integrations', 'Specialized', 'Performance'].map((c) => (
                <button
                  type="button"
                  key={c}
                  onClick={() => setNewCat(c)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 'var(--radius-pill)',
                    backgroundColor: newCat === c ? 'var(--accent-teal)' : 'var(--surface-recessed)',
                    color: newCat === c ? 'var(--text-on-accent)' : 'var(--text-secondary)',
                    border: '1px solid var(--border-subtle)',
                    fontSize: '12px',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <Textarea
            label="Description & Use Case *"
            placeholder="Explain the workflow problem or benefit this feature provides..."
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            rows={3}
            required
          />

          <Input
            label="Your Email (Private - for notification updates)"
            type="email"
            placeholder="alex@example.org"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
          />
        </form>
      </Modal>
    </div>
  );
};
